import React, { Component } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { computeBalancesByCurrency, isMemberReferencedInExpenses } from "../Models/ExpenseBalances";
import { slice1Actions } from "../store/reducer";
import "./TripMembersSection.css";

// The roster behind Expense Tracking's per-expense splits (ExpensesSection.js),
// but given its own guide-menu entry and screen — "recorded like
// Destinations" per the user's request — since who's on a trip is useful to
// keep as a record on its own, independent of whether any expenses ever get
// logged. `guide.tripMembers` is the single source of truth; this screen
// owns add/remove, ExpensesSection.js's pickers just read the resulting
// list. Balances shown here are pulled straight from guide.expenses via the
// same ExpenseBalances.js math Expenses itself uses — a historical summary
// of who paid/owes what, not something edited from this screen.
//
// Same local-state-then-explicit-save flow as ExpensesSection.js/
// DestinationsScreen.js — not a live Firestore write.
class TripMembersSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tripMembers: (window.guide_obj?.tripMembers || []).slice(),
      newMemberName: "",
      dirty: false,
      saveMessage: "",
    };
  }

  markDirty = () => this.setState({ dirty: true, saveMessage: "" });

  addMember = () => {
    const name = this.state.newMemberName.trim();
    if (name === "") return;
    const id = `tm_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
    this.setState(
      (prev) => ({ tripMembers: [...prev.tripMembers, { id, name }], newMemberName: "" }),
      this.markDirty
    );
  };

  removeMember = (memberId) => {
    if (isMemberReferencedInExpenses(memberId, window.guide_obj?.expenses || [])) {
      alert("This person is referenced in one or more expenses — remove them from those expenses first.");
      return;
    }
    this.setState(
      (prev) => ({ tripMembers: prev.tripMembers.filter((m) => m.id !== memberId) }),
      this.markDirty
    );
  };

  handleSave = () => {
    const { user_info } = this.props;
    window.guide_obj.tripMembers = this.state.tripMembers;
    if (user_info?.uid === "guest") window.guest_generated_unsaved_trip = true;
    this.props.dispatch(slice1Actions.setGuide({ guide: null }));
    this.setState({ dirty: false, saveMessage: "Saved to this guide — use \"Save Guide to Cloud\" to make it permanent." });
  };

  renderBalances = () => {
    const byCurrency = computeBalancesByCurrency(window.guide_obj?.expenses || [], this.state.tripMembers);
    const currencies = Object.keys(byCurrency);
    if (currencies.length === 0) return null;
    return (
      <>
        <span className="tms-section-label">Expense History</span>
        {currencies.map((currency) => (
          <div className="tms-card" key={currency}>
            <div className="tms-balance-currency">{currency}</div>
            {byCurrency[currency].map((row) => (
              <div className="tms-balance-row" key={row.memberId}>
                <span className="tms-balance-name">{row.name}</span>
                <span className="tms-balance-detail">
                  paid {row.paid.toFixed(2)} · owes {row.owed.toFixed(2)}
                </span>
                <span
                  className={`tms-balance-net${row.net < 0 ? " tms-balance-negative" : row.net > 0 ? " tms-balance-positive" : ""}`}
                >
                  {row.net > 0 ? "+" : ""}
                  {row.net.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        ))}
      </>
    );
  };

  render() {
    const { tripMembers, dirty, saveMessage } = this.state;
    return (
      <>
        <span className="tms-section-label">Trip Members</span>
        <div className="tms-card">
          <div className="tms-members-row">
            {tripMembers.map((m) => (
              <span className="tms-member-chip" key={m.id}>
                {m.name}
                <FontAwesomeIcon icon={solid("xmark")} className="tms-member-remove" onClick={() => this.removeMember(m.id)} />
              </span>
            ))}
            {tripMembers.length === 0 ? <span className="tms-empty">No trip members yet.</span> : null}
          </div>
          <div className="tms-add-member-row">
            <input
              className="tms-add-member-input"
              placeholder="Add a name…"
              value={this.state.newMemberName}
              onChange={(e) => this.setState({ newMemberName: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && this.addMember()}
            />
            <button className="tms-add-member-btn" onClick={this.addMember}>
              Add
            </button>
          </div>
        </div>

        {this.renderBalances()}

        <div className="tms-save-row">
          <button className="tms-save-button" onClick={this.handleSave} disabled={!dirty}>
            Save
          </button>
          {saveMessage ? <span className="tms-save-message">{saveMessage}</span> : null}
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  user_info: state.user_info,
});

export default connect(mapStateToProps)(TripMembersSection);
